/*
 * PdiTraining.jsx — Dedicated SEO landing page for ADI Part 3 / PDI Training,
 * explicitly targeting Sheffield & Rotherham for local search. Visible FAQ,
 * clear H1/H2 structure and internal links for crawlability.
 */
import { Link } from "react-router-dom";
import { GraduationCap, CheckCircle, Star, MapPin, ArrowRight } from "@phosphor-icons/react";
import { BRAND, waLink } from "../data";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { BookButton, WhatsAppButton } from "../components/site/Buttons";

const COVERS = [
  "Core competencies & the ADI Part 3 marking sheet",
  "Lesson planning, risk management and client-centred teaching",
  "Fault identification, analysis and correction",
  "Giving clear instruction and confident teaching skills",
  "Mock Part 3 sessions with honest, constructive feedback",
  "Building towards a confident, professional driving instructor career",
];

const FAQ = [
  {
    q: "Do you offer ADI Part 3 training in Sheffield and Rotherham?",
    a: "Yes. We provide ADI Part 3 and PDI training across both Sheffield and Rotherham, working around your schedule and preferred areas.",
  },
  {
    q: "What's the difference between PDI training and ADI Part 3?",
    a: "A PDI (Potential Driving Instructor) is working towards qualifying as an ADI. The Part 3 is the final teaching assessment — our training prepares you specifically for it with practical, supportive coaching.",
  },
  {
    q: "How much does PDI / ADI Part 3 training cost?",
    a: "PDI training lessons are £45 per hour. Get in touch for a tailored plan based on your current stage and goals.",
  },
  {
    q: "Is the training tailored to my level?",
    a: "Absolutely. Whether you're just starting out or polishing specific skills before your Part 3, we tailor every session to where you are.",
  },
];

export default function PdiTraining() {
  return (
    <div className="pt-[74px]">
      <Seo
        title="ADI Part 3 & PDI Training in Sheffield & Rotherham | Xclusive Driving School"
        description="PDI training and ADI Part 3 preparation in Sheffield & Rotherham. Practical, supportive coaching to help you qualify as a confident, professional driving instructor. £45/hr."
        canonical="https://xclusivedrivingschool.co.uk/adi-part-3-pdi-training"
      />

      {/* Hero */}
      <section className="bg-[#FBF7F4] py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#E4141B]/10 text-[#E4141B] font-head font-semibold text-sm px-4 py-2">
              <GraduationCap size={18} weight="fill" /> ADI Part 3 / PDI Training
            </span>
            <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-tight mt-6">
              ADI Part 3 &amp; PDI Training in <span className="text-[#E4141B]">Sheffield &amp; Rotherham</span>
            </h1>
            <p className="font-body text-lg text-[#4B4B52] leading-relaxed mt-5 max-w-2xl">
              Looking to become a driving instructor in Sheffield or Rotherham? Our ADI Part 3 and PDI training is designed to help you build the knowledge, confidence and teaching skills you need to progress towards your instructor career. With practical, supportive training tailored to your level, we'll help you develop into a confident and professional driving instructor.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <BookButton label="Enquire About PDI Training" to="/contact?lesson=PDI%20Training%20Lesson%20%E2%80%94%20%C2%A345" className="px-7 py-4 text-base" testid="pdi-enquire" />
              <WhatsAppButton className="px-7 py-4 text-base" text="Hi, I'd like to enquire about ADI Part 3 / PDI training." testid="pdi-whatsapp" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we cover */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <Reveal>
            <h2 className="font-head font-extrabold text-3xl md:text-4xl">What our PDI training covers</h2>
            <p className="font-body text-lg text-[#4B4B52] mt-3 max-w-2xl">
              Whether you're a PDI just starting out or preparing for your ADI Part 3 assessment, our training across Sheffield and Rotherham is built around you.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {COVERS.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <div className="flex items-start gap-3 bg-white border border-[#ECE6E2] rounded-2xl p-5 soft-shadow">
                  <CheckCircle size={22} weight="fill" className="text-[#25D366] mt-0.5 shrink-0" />
                  <span className="font-body text-[#17171A]">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-10 bg-[#FBF7F4] border border-[#ECE6E2] rounded-3xl p-8 flex flex-wrap items-center justify-between gap-6">
              <div>
                <span className="font-head font-semibold text-sm text-[#4B4B52] uppercase tracking-wide">PDI Training Lesson</span>
                <div className="font-head font-extrabold text-4xl text-[#E4141B] mt-1">£45 <span className="text-lg text-[#4B4B52] font-semibold">per hour</span></div>
              </div>
              <div className="flex items-center gap-2 text-[#17171A] font-head font-semibold">
                <MapPin size={20} weight="fill" className="text-[#E4141B]" /> Sheffield &amp; Rotherham
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <Reveal>
            <h2 className="font-head font-extrabold text-3xl md:text-4xl">ADI Part 3 / PDI training — your questions</h2>
          </Reveal>
          <div className="mt-8 flex flex-col gap-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="bg-white border border-[#ECE6E2] rounded-2xl p-6 soft-shadow">
                  <h3 className="font-head font-bold text-lg">{f.q}</h3>
                  <p className="font-body text-[#4B4B52] mt-2 leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <div className="bg-[#17171A] rounded-[32px] p-10 md:p-14 text-center text-white">
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} size={22} weight="fill" className="text-[#FBBC05]" />)}
            </div>
            <h2 className="font-head font-extrabold text-2xl md:text-3xl">Start your instructor journey in Sheffield or Rotherham</h2>
            <p className="font-body text-lg text-white/70 mt-3 max-w-xl mx-auto">
              Get in touch to arrange your ADI Part 3 / PDI training. Call {BRAND.phone}, message us on WhatsApp, or send an enquiry.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/contact?lesson=PDI%20Training%20Lesson%20%E2%80%94%20%C2%A345" data-testid="pdi-cta-contact" className="inline-flex items-center gap-2 rounded-full bg-[#E4141B] px-7 py-4 font-head font-semibold hover:bg-[#B70F15] transition-colors">
                Send an Enquiry <ArrowRight size={17} weight="bold" />
              </Link>
              <a href={BRAND.phoneHref} className="rounded-full border-2 border-white/40 text-white px-7 py-4 font-head font-semibold hover:bg-white/10 transition">Call {BRAND.phone}</a>
            </div>
            <div className="mt-6">
              <Link to="/driving-lessons" className="font-head font-semibold text-sm text-white/70 hover:text-white underline">See all our driving lessons</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
